export const CHAT_UI_ACTION_LABEL_PREFIX = "chat-ui:" as const;
export const CHAT_ATTACHED_CONNECTORS_STORAGE_PREFIX =
  "fintheon:chat-attached-connectors:v1:" as const;
export const CHAT_CONNECTOR_REQUEST_FIELDS = [
  "activeConnectors",
  "mcpServers",
] as const;

export const chatUiToolNames = [
  "open_todo_drawer",
  "propose_plan",
  "open_right_rail",
  "present_x_post",
  "write_evidence_artifact",
  "ask_approval_questions",
  "write_session_artifact",
  "render_ui_operations",
  "narrativeflow_open_surface",
  "narrativeflow_show_internal_data",
  "narrativeflow_stage_edit",
] as const;
export const chatUiActionNames = [
  "open_todo_drawer",
  "plan_proposal",
  "plan_proposal_resolved",
  "open_right_rail",
  "present_x_post",
  "approval_questions",
  "approval_questions_resolved",
  "narrativeflow_open_surface",
  "narrativeflow_show_internal_data",
  "narrativeflow_stage_edit",
  "narrativeflow_apply_approved_edit",
] as const;
export const narrativeFlowChatUiActionNames = [
  "narrativeflow_open_surface",
  "narrativeflow_show_internal_data",
  "narrativeflow_stage_edit",
  "narrativeflow_apply_approved_edit",
] as const;
export const chatUiToolActionMap = {
  open_todo_drawer: "open_todo_drawer",
  propose_plan: "plan_proposal",
  open_right_rail: "open_right_rail",
  present_x_post: "present_x_post",
  write_evidence_artifact: "open_right_rail",
  ask_approval_questions: "approval_questions",
  write_session_artifact: "open_right_rail",
  render_ui_operations: "open_right_rail",
  narrativeflow_open_surface: "narrativeflow_open_surface",
  narrativeflow_show_internal_data: "narrativeflow_show_internal_data",
  narrativeflow_stage_edit: "narrativeflow_stage_edit",
} as const satisfies Record<ChatUiToolName, ChatUiActionName>;

export type ChatUiToolName = (typeof chatUiToolNames)[number];
export type ChatUiActionName = (typeof chatUiActionNames)[number];
export type NarrativeFlowChatUiAction =
  (typeof narrativeFlowChatUiActionNames)[number];
export type ChatConnectorRequestField =
  (typeof CHAT_CONNECTOR_REQUEST_FIELDS)[number];

export interface ChatUiTodoItemContract {
  text: string;
  issueType?: "task" | "bug" | "feature" | "risk" | "chore" | "issue";
  priority?: string;
}

export interface ChatUiQuestionContract {
  id: string;
  label: string;
  question: string;
  placeholder?: string;
  required?: boolean;
}

export interface ChatUiAnswerContract {
  id: string;
  label: string;
  value: string;
}

export type ChatUiQuestionStatus = "answered" | "cancelled" | "expired";
export type ChatUiPlanDecisionStatus = "approved" | "denied" | "expired";
export type NarrativeFlowSurface =
  "workspace" | "forecasts" | "coliseum" | "resolved" | "map";

export interface NarrativeFlowStageEditContract {
  surface: "workspace" | "forecasts" | "resolved" | "map";
  editType:
    | "workspace_title"
    | "workspace_summary"
    | "workspace_docs"
    | "workspace_flow"
    | "workspace_timeline"
    | "workspace_bundle"
    | "deskmap_note"
    | "forecast_draft"
    | "resolved_note";
  targetId?: string;
  previewMarkdown: string;
  patch: Record<string, unknown>;
  rationale: string;
  operationId: string;
  requiresApproval: true;
}

export interface ChatUiActionPayloadByAction {
  open_todo_drawer: {
    title?: string;
    items: ChatUiTodoItemContract[];
  };
  plan_proposal: {
    actionId: string;
    title: string;
    summary?: string;
    markdown: string;
    approvalLabel?: string;
    denyLabel?: string;
    surface?: "chat" | "narrativeflow";
    workspaceId?: string;
  };
  plan_proposal_resolved: {
    actionId: string;
    status: ChatUiPlanDecisionStatus;
    feedback?: string;
  };
  open_right_rail: {
    title: string;
    surface?: "plan" | "canvas" | "browser" | "report";
    markdown: string;
    append?: boolean;
    artifactId?: string;
  };
  present_x_post: {
    url: string;
    artifactId?: string;
    title?: string;
    author?: string;
    text?: string;
    reason?: string;
    openRail?: boolean;
  };
  approval_questions: {
    actionId: string;
    title: string;
    summary?: string;
    questions: ChatUiQuestionContract[];
  };
  approval_questions_resolved: {
    actionId: string;
    status: ChatUiQuestionStatus;
    answerCount: number;
  };
  narrativeflow_open_surface: {
    surface: NarrativeFlowSurface;
    sessionId?: string;
    focus?: string;
  };
  narrativeflow_show_internal_data: {
    title: string;
    surface?: NarrativeFlowSurface;
    dataKind: string;
    markdown: string;
    payload?: Record<string, unknown>;
    append?: boolean;
  };
  narrativeflow_stage_edit: NarrativeFlowStageEditContract;
  narrativeflow_apply_approved_edit: NarrativeFlowStageEditContract & {
    approvalActionId?: string;
    approvedAt: string;
  };
}

export type ChatUiActionPayload<Action extends ChatUiActionName> =
  ChatUiActionPayloadByAction[Action];
export type ChatUiActionDetail<Action extends ChatUiActionName> =
  ChatUiActionPayload<Action> & { action: Action };

export interface ChatUiCognitionStepLike {
  kind?: string;
  label?: string;
  detail?: string;
  ts?: number;
}

export type ChatUiActionParseResult =
  | {
      ok: true;
      action: ChatUiActionName;
      detail: ChatUiActionDetail<ChatUiActionName>;
    }
  | { ok: false; reason: string; shouldWarn: boolean };

const chatUiActionSet = new Set<string>(chatUiActionNames);
const narrativeFlowActionSet = new Set<string>(narrativeFlowChatUiActionNames);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isChatUiActionName(value: unknown): value is ChatUiActionName {
  return typeof value === "string" && chatUiActionSet.has(value);
}

export function isNarrativeFlowChatUiAction(
  value: unknown,
): value is NarrativeFlowChatUiAction {
  return typeof value === "string" && narrativeFlowActionSet.has(value);
}

export function makeChatUiActionLabel(action: ChatUiActionName): string {
  return `${CHAT_UI_ACTION_LABEL_PREFIX}${action}`;
}

export function makeChatUiActionDetail<Action extends ChatUiActionName>(
  action: Action,
  payload: ChatUiActionPayload<Action>,
): ChatUiActionDetail<Action> {
  return { ...payload, action };
}

function actionFromLabel(label: string | undefined): ChatUiActionName | null {
  if (!label?.startsWith(CHAT_UI_ACTION_LABEL_PREFIX)) return null;
  const action = label.slice(CHAT_UI_ACTION_LABEL_PREFIX.length);
  return isChatUiActionName(action) ? action : null;
}

export function parseChatUiActionStep(
  step: ChatUiCognitionStepLike,
): ChatUiActionParseResult {
  const hasChatUiLabel = step.label?.startsWith(CHAT_UI_ACTION_LABEL_PREFIX);
  if (step.kind !== "tool-dispatch") {
    return { ok: false, reason: "not a tool-dispatch step", shouldWarn: false };
  }
  if (!step.detail) {
    return {
      ok: false,
      reason: "missing detail payload",
      shouldWarn: !!hasChatUiLabel,
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(step.detail);
  } catch {
    return {
      ok: false,
      reason: "detail is not valid JSON",
      shouldWarn: !!hasChatUiLabel,
    };
  }

  if (!isRecord(parsed)) {
    return {
      ok: false,
      reason: "detail is not an object",
      shouldWarn: !!hasChatUiLabel,
    };
  }

  const labelAction = actionFromLabel(step.label);
  const rawAction = parsed.action;
  const hasAction = typeof rawAction === "string";
  if (!labelAction && !hasAction) {
    return { ok: false, reason: "not a chat UI action", shouldWarn: false };
  }
  if (hasAction && !isChatUiActionName(rawAction)) {
    return {
      ok: false,
      reason: `unknown action ${rawAction}`,
      shouldWarn: true,
    };
  }
  if (step.label?.startsWith(CHAT_UI_ACTION_LABEL_PREFIX) && !labelAction) {
    return {
      ok: false,
      reason: `unknown label ${step.label}`,
      shouldWarn: true,
    };
  }
  if (labelAction && hasAction && labelAction !== rawAction) {
    return {
      ok: false,
      reason: `label/action mismatch ${labelAction} != ${rawAction}`,
      shouldWarn: true,
    };
  }

  const action = (rawAction as ChatUiActionName | undefined) ?? labelAction;
  if (!action) {
    return { ok: false, reason: "missing action", shouldWarn: true };
  }

  return {
    ok: true,
    action,
    detail: { ...parsed, action } as ChatUiActionDetail<ChatUiActionName>,
  };
}
